import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import MainLayout from "../layout/MainLayout";
import { useAlertContext } from "../context/AlertContext";
import KpiCardEditor from "../components/kpi/KpiCardEditor";
import { createKpiCard, deleteKpiCard, getKpiMonth, saveKpiCard, updateKpiMonth } from "../services/kpiApi";
import { deltaClass, emptyCard, money, percent, periodLabel, plain } from "../utils/kpiFormat";

/**
 * KPI: месяц одной таблицы КПЭ — грейды и ставки, правила, карты сотрудников. [Claude] 2026-11-02, Чат 6, №10.
 */

const cell = "w-full rounded border border-slate-200 bg-white px-1.5 py-1 text-sm outline-none focus:border-amber-400 disabled:bg-slate-50";
const btn = "min-h-9 rounded-lg px-3 py-1.5 text-sm font-semibold transition disabled:opacity-50";
const btnGhost = `${btn} border border-slate-200 bg-white text-slate-700 hover:border-slate-400`;
const btnMain = `${btn} bg-[#e0bb48] text-black hover:bg-amber-400`;

const RULE_FIELDS = [
    ["fixed_share_pct", "Доля постоянной части в окладе, %"],
    ["coef_threshold_pct", "Порог коэф. раб. времени, %"],
    ["overperf_bonus_pct", "+ к лимиту при подтверждённом перевыполнении, %"],
    ["max_paid_pct", "Максимум оплачиваемого %, при нормальном коэф."],
    ["extra_cap_pct", "Лимит доп. задач, % от постоянной части"],
];

function SettingsPanel({ month, readOnly, onSave }) {
    const [grades, setGrades] = useState(month.grades || []);
    const [rules, setRules] = useState(month.rules || {});
    const [notes, setNotes] = useState(month.notes || "");
    const [busy, setBusy] = useState(false);
    useEffect(() => { setGrades(month.grades || []); setRules(month.rules || {}); setNotes(month.notes || ""); }, [month]);
    const setGrade = (i, patch) => setGrades((g) => g.map((x, j) => (j === i ? { ...x, ...patch } : x)));

    const save = async () => {
        setBusy(true);
        try { await onSave({ grades, rules, notes }); } finally { setBusy(false); }
    };

    return (
        <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-4">
            <div>
                <h3 className="font-semibold text-slate-800 mb-2">Грейды и стоимость часа</h3>
                <div className="overflow-x-auto">
                    <table className="min-w-[900px] w-full text-sm">
                        <thead className="text-xs text-slate-500">
                            <tr className="text-left">
                                <th className="px-2 py-1 w-14">Грейд</th>
                                <th className="px-2 py-1 w-40">Название</th>
                                <th className="px-2 py-1">Описание</th>
                                <th className="px-2 py-1">Пример задач</th>
                                <th className="px-2 py-1 w-24">₽/ч раб. время</th>
                                <th className="px-2 py-1 w-24">₽/ч доп. время</th>
                            </tr>
                        </thead>
                        <tbody>
                            {grades.map((g, i) => (
                                <tr key={g.code} className="border-t border-slate-100 align-top">
                                    <td className="px-2 py-1 font-semibold">{g.code}</td>
                                    <td className="px-1 py-1"><input className={cell} value={g.name || ""} disabled={readOnly} onChange={(e) => setGrade(i, { name: e.target.value })} /></td>
                                    <td className="px-1 py-1"><textarea className={`${cell} min-h-[48px]`} value={g.description || ""} disabled={readOnly} onChange={(e) => setGrade(i, { description: e.target.value })} /></td>
                                    <td className="px-1 py-1"><textarea className={`${cell} min-h-[48px]`} value={g.example || ""} disabled={readOnly} onChange={(e) => setGrade(i, { example: e.target.value })} /></td>
                                    <td className="px-1 py-1"><input className={`${cell} text-right`} value={g.rate_work ?? ""} disabled={readOnly} onChange={(e) => setGrade(i, { rate_work: e.target.value })} /></td>
                                    <td className="px-1 py-1"><input className={`${cell} text-right`} value={g.rate_extra ?? ""} disabled={readOnly} onChange={(e) => setGrade(i, { rate_extra: e.target.value })} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            <div>
                <h3 className="font-semibold text-slate-800 mb-2">Правила расчёта</h3>
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
                    {RULE_FIELDS.map(([k, label]) => (
                        <label key={k} className="text-xs text-slate-500">{label}
                            <input className={`${cell} mt-1 text-right`} value={rules[k] ?? ""} disabled={readOnly}
                                onChange={(e) => setRules((r) => ({ ...r, [k]: e.target.value }))} />
                        </label>
                    ))}
                </div>
                <p className="mt-2 text-xs text-slate-500">
                    Если коэф. раб. времени ниже порога — оплачиваемый % не выше коэф. (плюс надбавка при подтверждённом перевыполнении);
                    иначе — не выше максимума.
                </p>
            </div>
            <label className="block text-xs text-slate-500">Заметки к месяцу
                <textarea className={`${cell} mt-1 min-h-[56px]`} value={notes} disabled={readOnly} onChange={(e) => setNotes(e.target.value)} />
            </label>
            {!readOnly && <button type="button" className={btnMain} onClick={save} disabled={busy}>{busy ? "Сохраняю…" : "Сохранить и пересчитать карты"}</button>}
        </div>
    );
}

export default function KpiMonthPage() {
    const { monthId } = useParams();
    const [params, setParams] = useSearchParams();
    const cardId = params.get("card");
    const { showAlert } = useAlertContext();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showSettings, setShowSettings] = useState(false);

    const load = useCallback(async () => {
        try {
            setData(await getKpiMonth(monthId));
        } catch (e) {
            showAlert?.(e?.message || "Не удалось загрузить месяц", "error");
        } finally {
            setLoading(false);
        }
    }, [monthId, showAlert]);

    useEffect(() => { load(); }, [load]);

    const month = data?.month;
    const closed = month?.status === "closed";
    // №35: уровень доступа к таблице (1 — смотрит, 2 — правит, 3 — управляет); старый backend уровня не отдаёт.
    const canEdit = (data?.group?.level ?? 2) >= 2;
    const readOnly = closed || !canEdit;
    const cards = data?.cards || [];
    const selected = cards.find((c) => String(c.id) === String(cardId)) || null;
    const openCard = (id) => setParams(id ? { card: String(id) } : {});

    const replaceCard = (saved) => setData((d) => ({ ...d, cards: d.cards.map((c) => (c.id === saved.id ? saved : c)) }));

    const save = async (draft) => {
        setSaving(true);
        try {
            const saved = await saveKpiCard(draft.id, draft);
            replaceCard(saved);
            showAlert?.("Карта сохранена", "success");
            load();
        } catch (e) {
            showAlert?.(e?.message || "Не удалось сохранить", "error");
        } finally {
            setSaving(false);
        }
    };

    const addCard = async () => {
        try {
            const created = await createKpiCard(monthId, emptyCard());
            await load();
            openCard(created.id);
        } catch (e) {
            showAlert?.(e?.message || "Не удалось добавить карту", "error");
        }
    };

    const removeCard = async (card) => {
        if (!window.confirm(`Удалить карту «${card.title}»?`)) return;
        try {
            await deleteKpiCard(card.id);
            openCard(null);
            load();
        } catch (e) {
            showAlert?.(e?.message || "Не удалось удалить", "error");
        }
    };

    const saveMonth = async (patch) => {
        try {
            setData(await updateKpiMonth(monthId, patch));
            showAlert?.("Сохранено, карты пересчитаны", "success");
        } catch (e) {
            showAlert?.(e?.message || "Не удалось сохранить", "error");
        }
    };

    if (loading) return <MainLayout><p className="text-sm text-slate-500">Загрузка…</p></MainLayout>;
    if (!data) return <MainLayout><p className="text-sm text-slate-500">Месяц не найден. <Link className="underline" to="/kpi">К KPI</Link></p></MainLayout>;

    return (
        <MainLayout>
            <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-2">
                    <Link to={`/kpi?period=${month.period}`} className="text-sm text-slate-500 hover:text-slate-800">← KPI · {periodLabel(month.period)}</Link>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <h1 className="text-xl font-bold text-slate-900">{data.group?.name} — {periodLabel(month.period)}</h1>
                    {closed && <span className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600">месяц закрыт</span>}
                    {!canEdit && <span className="rounded bg-sky-50 px-2 py-0.5 text-xs text-sky-700">только просмотр</span>}
                    {data.group?.source_url && (
                        <a href={data.group.source_url} target="_blank" rel="noreferrer" className="text-xs text-slate-500 underline">Google-таблица (источник)</a>
                    )}
                    <div className="flex-1" />
                    <button type="button" className={btnGhost} onClick={() => setShowSettings((v) => !v)}>
                        {showSettings ? "Скрыть грейды и правила" : "Грейды и правила"}
                    </button>
                    {canEdit && (
                        <button type="button" className={btnGhost} onClick={() => saveMonth({ status: closed ? "open" : "closed" })}>
                            {closed ? "Открыть месяц" : "Закрыть месяц"}
                        </button>
                    )}
                    {!readOnly && <button type="button" className={btnMain} onClick={addCard}>+ Карта</button>}
                </div>

                <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
                    {[["План по КПЭ", money(data.summary.kpi_plan), ""], ["Расчёт", money(data.summary.kpi_calc), ""],
                        ["Дельта", money(data.summary.kpi_delta), deltaClass(data.summary.kpi_delta)],
                        ["Доп. задачи", money(data.summary.extra_tasks_paid), ""], ["К оплате", money(data.summary.total_pay), "text-slate-900"]]
                        .map(([l, v, cls]) => (
                            <div key={l} className="rounded-lg bg-white border border-slate-200 px-3 py-2">
                                <p className="text-xs text-slate-500">{l}</p>
                                <p className={`text-lg font-bold ${cls || "text-slate-800"}`}>{v}</p>
                            </div>
                        ))}
                </div>

                {showSettings && <SettingsPanel month={month} readOnly={readOnly} onSave={saveMonth} />}

                <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
                    <table className="min-w-full text-sm">
                        <thead className="text-xs text-slate-500">
                            <tr className="text-left">
                                <th className="px-4 py-2 font-medium">Карта</th>
                                <th className="px-2 py-2 font-medium">Грейд</th>
                                <th className="px-2 py-2 font-medium text-right">Оклад</th>
                                <th className="px-2 py-2 font-medium text-right">Коэф.</th>
                                <th className="px-2 py-2 font-medium text-right">Баллы</th>
                                <th className="px-2 py-2 font-medium text-right">План</th>
                                <th className="px-2 py-2 font-medium text-right">Расчёт</th>
                                <th className="px-2 py-2 font-medium text-right">Дельта</th>
                                <th className="px-2 py-2 font-medium text-right">Доп.</th>
                                <th className="px-4 py-2 font-medium text-right">К оплате</th>
                            </tr>
                        </thead>
                        <tbody>
                            {cards.map((c) => (
                                <tr key={c.id} onClick={() => openCard(c.id)}
                                    className={`border-t border-slate-100 cursor-pointer ${selected?.id === c.id ? "bg-amber-50" : "hover:bg-amber-50/40"}`}>
                                    <td className="px-4 py-2">
                                        <p className="font-medium text-slate-800">{c.employee_name || c.title}</p>
                                        {c.employee_name && c.title !== c.employee_name && <p className="text-xs text-slate-500">{c.title}</p>}
                                        {c.sheet_diff && Object.keys(c.sheet_diff).length > 0 && <p className="text-xs text-amber-700">есть расхождение с таблицей</p>}
                                    </td>
                                    <td className="px-2 py-2">{c.grade || "—"}</td>
                                    <td className="px-2 py-2 text-right">{money(c.salary)}</td>
                                    <td className="px-2 py-2 text-right">{percent(c.work_coef)}</td>
                                    <td className="px-2 py-2 text-right">{plain(c.points_fact_total, 0)} / {plain(c.points_plan_total, 0)}</td>
                                    <td className="px-2 py-2 text-right">{money(c.kpi_plan)}</td>
                                    <td className="px-2 py-2 text-right">{money(c.kpi_calc)}</td>
                                    <td className={`px-2 py-2 text-right ${deltaClass(c.kpi_delta)}`}>{money(c.kpi_delta)}</td>
                                    <td className="px-2 py-2 text-right">{money(c.extra_tasks_paid)}</td>
                                    <td className="px-4 py-2 text-right font-semibold">
                                        {money(c.total_pay)}
                                        {c.sheet_diff?.total_pay && (
                                            <p className="text-xs font-normal text-amber-700" title="Сумма в Google-таблице при переносе">
                                                в таблице {money(c.sheet_diff.total_pay.sheet)}
                                            </p>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {!cards.length && <tr><td colSpan={10} className="px-4 py-3 text-slate-500">Карт нет</td></tr>}
                        </tbody>
                    </table>
                </div>

                {selected && (
                    <div className="rounded-xl border border-amber-200 bg-white p-4">
                        <KpiCardEditor monthId={month.id} card={selected} readOnly={readOnly} saving={saving}
                            onSave={save} onDelete={() => removeCard(selected)} onClose={() => openCard(null)} />
                    </div>
                )}
            </div>
        </MainLayout>
    );
}
